import React from 'react'

import { omit } from 'ramda'
import { components, OptionProps, GroupBase } from 'react-select'
import styled, { css } from 'styled-components'

import { SelectOptionInterface } from '@customTypes'
import { Checkbox } from '_components/checkbox'
import { palette } from '_styles/variables'

interface StyledOptionComponent {
  <
    Op extends SelectOptionInterface,
    IsMulti extends boolean = false,
    Group extends GroupBase<Op> = GroupBase<Op>
  >(
    props: OptionProps<Op, IsMulti, Group> & {
      isLastSelected?: boolean
    }
  ): JSX.Element
}

const StyledCheckbox = styled(Checkbox)`
  pointer-events: none;
`

const StyledOption: StyledOptionComponent = styled(components.Option)<{
  isLastSelected?: boolean
}>`
  padding: 0.5rem 0.75rem;
  padding-bottom: ${({ isLastSelected }) =>
    isLastSelected ? 'calc(0.5rem - 1px)' : '0.5rem'};
  ${({ isLastSelected }) =>
    isLastSelected &&
    css`
      border-bottom: 1px solid ${palette.neutral['grey-ebebeb']};
    `}
`

export const getOptions = (lastSelectedOptions?: (string | number)[]) => {
  const Option = <
    Op extends SelectOptionInterface,
    IsMulti extends boolean = false,
    Group extends GroupBase<Op> = GroupBase<Op>
  >(
    props: OptionProps<Op, IsMulti, Group>
  ) => {
    const { isMulti, children, isSelected, innerProps } = props

    const newProps: typeof props = {
      ...props,
      innerProps: omit(['onMouseMove', 'onMouseOver'], innerProps),
    }

    if (isMulti) {
      const checkboxLabel = children?.toString() as string

      const isLastSelected =
        isSelected &&
        lastSelectedOptions &&
        Array.isArray(lastSelectedOptions) &&
        lastSelectedOptions.includes(checkboxLabel)

      const multiInnerProps: typeof innerProps = {
        ...newProps.innerProps,
        'aria-label': checkboxLabel,
        title: checkboxLabel,
      }

      return (
        <StyledOption
          {...newProps}
          innerProps={multiInnerProps}
          isLastSelected={isLastSelected}
          isMulti
        >
          <StyledCheckbox label={checkboxLabel} checked={isSelected} readOnly />
        </StyledOption>
      )
    }

    return <StyledOption {...newProps} />
  }

  return Option as typeof components.Option
}
